import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
    Clock,
    Cloud,
    Monitor,
    Network,
    Plus,
    RefreshCw,
    Shield,
    ShieldAlert,
    ShieldCheck,
    Users,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    useRegistries,
    useCreateRegistry,
    type RegistryProfile,
} from '@/hooks/use-registries';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { AddRegistryForm } from '@/components/registry/AddRegistryForm';
import { useHeaderAction } from '@/lib/header-action-context';
import { usePageTitle } from '@/hooks/use-page-title';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

const registryCardStyle = {
    accent: 'border-l-blue-500',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
};

const kindConfig: Record<string, { icon: typeof Cloud; label: string; color: string }> = {
    local: { icon: Monitor, label: 'Local', color: 'text-emerald-600' },
    cloud: { icon: Cloud, label: 'Cloud', color: 'text-blue-600' },
    partner: { icon: Users, label: 'Partner', color: 'text-purple-600' },
};

const statusBadgeStyles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    disabled: 'bg-slate-100 text-slate-600',
    unreachable: 'bg-red-100 text-red-700',
};

const trustConfig: Record<string, { icon: typeof Shield; label: string }> = {
    trusted: { icon: ShieldCheck, label: 'Trusted' },
    verified: { icon: Shield, label: 'Verified' },
    untrusted: { icon: ShieldAlert, label: 'Untrusted' },
};

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'status', label: 'Status' },
    { key: 'kind', label: 'Kind' },
    { key: 'modified', label: 'Updated' },
] as const;

type SortKey = (typeof SORT_OPTIONS)[number]['key'];

function formatRelativeTime(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
}

function RegistryCard({ registry }: { registry: RegistryProfile }) {
    const kc = kindConfig[registry.kind] ?? kindConfig.cloud;
    const KindIcon = kc.icon;
    const badgeStyle = statusBadgeStyles[registry.status] ?? statusBadgeStyles.active;
    const tc = trustConfig[registry.trust_tier] ?? trustConfig.untrusted;
    const TrustIcon = tc.icon;

    return (
        <Link to={`/registries/${registry.id}`} className="block">
            <Card
                className={`relative w-full min-h-32 border border-slate-200 p-3 border-l-4 ${registryCardStyle.accent} text-foreground/90 transition-all duration-200 hover:cursor-pointer hover:border-slate-300 hover:bg-slate-50 shadow-sm hover:shadow-md hover:shadow-blue-100/50`}
            >
                <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
                    {registry.is_default && (
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium uppercase text-slate-600">
                            Default
                        </span>
                    )}
                    <span
                        className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase ${badgeStyle}`}
                    >
                        {registry.status}
                    </span>
                </div>

                <div className="flex flex-row items-start gap-2">
                    <div
                        className={`flex shrink-0 items-center justify-center rounded-md p-1.5 ${registryCardStyle.iconBg} ${registryCardStyle.iconColor}`}
                    >
                        <KindIcon size={18} />
                    </div>
                    <div className="flex flex-1 flex-col min-w-0">
                        <div className="text-left text-sm font-bold text-slate-800 leading-tight truncate">
                            {registry.display_name ?? registry.alias}
                        </div>
                        <div className="text-left text-[10px] text-slate-500">
                            <span className="font-mono">{registry.alias}</span>
                        </div>
                    </div>
                </div>

                <div className="mt-auto flex items-center gap-1.5 border-t border-slate-100 pt-1.5 text-[10px] text-slate-500">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className={kc.color}>{kc.label}</span>
                        <span className="flex items-center gap-0.5 shrink-0">
                            <TrustIcon size={10} />
                            {tc.label}
                        </span>
                        {registry.supports_federation && (
                            <span className="flex items-center gap-0.5 text-blue-600 shrink-0">
                                <Network size={10} />
                                Fed
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 text-slate-400 shrink-0">
                        <Clock size={10} />
                        <span>{formatRelativeTime(registry.modified)}</span>
                    </div>
                </div>
            </Card>
        </Link>
    );
}

export default function RegistriesListPage() {
    const { data: registries, isLoading, refetch, isRefetching } = useRegistries();
    const createMutation = useCreateRegistry();
    const [dialogOpen, setDialogOpen] = useState(false);
    const { setHeaderAction, setHeaderTitle } = useHeaderAction();
    usePageTitle('Registries');

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [kindFilter, setKindFilter] = useState('');
    const [sortField, setSortField] = useState<SortKey>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const handleCreate = async (data: Partial<RegistryProfile>) => {
        await createMutation.mutateAsync(data);
        setDialogOpen(false);
    };

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field as SortKey);
            setSortDirection('asc');
        }
    };

    useEffect(() => {
        setHeaderTitle({
            icon: (
                <div
                    className={`flex h-7 w-7 items-center justify-center rounded-md ${registryCardStyle.iconBg} ${registryCardStyle.iconColor}`}
                >
                    <Network size={16} />
                </div>
            ),
            title: 'Registries',
            subtitle: 'Browse and manage the registry capability list.',
        });

        setHeaderAction(
            <div className="flex items-center gap-2">
                <button
                    onClick={() => refetch()}
                    disabled={isRefetching}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                >
                    <RefreshCw
                        size={14}
                        className={isRefetching ? 'animate-spin' : ''}
                    />
                    Refresh
                </button>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <button className="flex items-center gap-2 rounded-lg bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-btn-primary-hover">
                            <Plus size={14} />
                            Add Registry
                        </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                            <DialogTitle>Add Registry Connection</DialogTitle>
                        </DialogHeader>
                        <AddRegistryForm
                            onSubmit={handleCreate}
                            isSubmitting={createMutation.isPending}
                        />
                    </DialogContent>
                </Dialog>
            </div>,
        );
        return () => {
            setHeaderAction(null);
            setHeaderTitle(null);
        };
    }, [refetch, isRefetching, dialogOpen, createMutation.isPending, setHeaderAction, setHeaderTitle]);

    const filtered = useMemo(() => {
        if (!registries) return [];
        let items = [...registries];

        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            items = items.filter(
                (r) =>
                    (r.display_name ?? '').toLowerCase().includes(q) ||
                    r.alias.toLowerCase().includes(q) ||
                    (r.zone ?? '').toLowerCase().includes(q),
            );
        }
        if (statusFilter) {
            items = items.filter((r) => r.status === statusFilter);
        }
        if (kindFilter) {
            items = items.filter((r) => r.kind === kindFilter);
        }

        items.sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'name':
                    cmp = (a.display_name ?? a.alias).localeCompare(b.display_name ?? b.alias);
                    break;
                case 'status':
                    cmp = a.status.localeCompare(b.status);
                    break;
                case 'kind':
                    cmp = a.kind.localeCompare(b.kind);
                    break;
                case 'modified':
                    cmp = new Date(a.modified).getTime() - new Date(b.modified).getTime();
                    break;
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });

        return items;
    }, [registries, searchQuery, statusFilter, kindFilter, sortField, sortDirection]);

    const hasActiveFilters = searchQuery || statusFilter || kindFilter;

    const extraFilters = (
        <div className="flex flex-wrap items-center gap-3">
            <Select
                value={statusFilter || 'all'}
                onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
            >
                <SelectTrigger className={cn('w-44', listControlsGlassTrigger)}>
                    <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                    <SelectItem value="unreachable">Unreachable</SelectItem>
                </SelectContent>
            </Select>

            <Select
                value={kindFilter || 'all'}
                onValueChange={(v) => setKindFilter(v === 'all' ? '' : v)}
            >
                <SelectTrigger className={cn('w-40', listControlsGlassTrigger)}>
                    <SelectValue placeholder="All Kinds" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All Kinds</SelectItem>
                    <SelectItem value="local">Local</SelectItem>
                    <SelectItem value="cloud">Cloud</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                </SelectContent>
            </Select>
        </div>
    );

    return (
        <CapabilityListPanel<RegistryProfile>
            items={filtered}
            sortOptions={SORT_OPTIONS.map((option) => ({
                key: option.key,
                label: option.label,
            }))}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search registries by name, alias, or zone..."
            allTags={[]}
            selectedTags={new Set<string>()}
            onToggleTag={() => {}}
            onClearAllTags={() => {}}
            onSelectAllTags={() => {}}
            showTagsFilter={false}
            extraToolbar={extraFilters}
            isLoading={isLoading}
            loadingMessage="Loading registries..."
            emptyStateVariant="glass"
            emptyMessage={
                hasActiveFilters
                    ? 'No registries match the current filters.'
                    : 'No registries configured. Add a registry to start federating capabilities.'
            }
            renderCard={(registry) => (
                <RegistryCard key={registry.id} registry={registry} />
            )}
        />
    );
}
